/**
 * PaintRequestDetailModal — read-only inspection of a single paint batch.
 *
 * Phase C is view-only. Edit / cancel batch actions land in later phases.
 */

import Modal from '../../common/Modal';
import { PaintStatusBadge } from './PaintActivityPanel';

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

function fmtQty(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  return n % 1 === 0 ? String(Math.round(n)) : n.toString();
}

function Field({ label, children, mono = false }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs uppercase tracking-wider text-gray-400">{label}</span>
      <span className={`text-sm text-gray-800 ${mono ? 'font-mono' : ''}`}>{children || '—'}</span>
    </div>
  );
}

export default function PaintRequestDetailModal({ open, onClose, paintRequest }) {
  if (!open || !paintRequest) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <span className="font-mono text-blue-700">
          Paint Request {paintRequest.paint_request_number}
        </span>
      }
      maxWidth="max-w-xl"
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <PaintStatusBadge status={paintRequest.status} size="lg" />
          <span className="text-xs text-gray-500">
            Sent {fmtDateTime(paintRequest.sent_at)}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          <Field label="Vendor">{paintRequest.vendor_name}</Field>
          <Field label="Job">#{paintRequest.job_number}</Field>
          <Field label="Pump system part #" mono>
            {paintRequest.pump_system_part_number}
          </Field>
          <Field label="Unit description">{paintRequest.unit_description}</Field>
          <Field label="Quantity sent">{fmtQty(paintRequest.qty_sent)}</Field>
          <Field label="Paint color">{paintRequest.paint_color}</Field>
        </div>

        {paintRequest.special_instructions && (
          <div>
            <span className="text-xs uppercase tracking-wider text-gray-400 block mb-1">
              Special instructions
            </span>
            <div className="text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded p-2 whitespace-pre-wrap">
              {paintRequest.special_instructions}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-x-4 gap-y-3 pt-3 border-t border-gray-100">
          <Field label="Created">{fmtDateTime(paintRequest.created_at)}</Field>
          <Field label="Print queue ID" mono>
            {paintRequest.print_queue_id
              ? String(paintRequest.print_queue_id).slice(0, 8)
              : '—'}
          </Field>
        </div>
      </div>

      <div className="flex justify-end pt-4 mt-4 border-t border-gray-100">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-md text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
        >
          Close
        </button>
      </div>
    </Modal>
  );
}
